import React from 'react';
import { LegalLayout } from '../components/LegalLayout';

export function PrivacyPolicy() {
  return (
    <LegalLayout>
      <div className="prose prose-invert prose-lg max-w-none">
        <h1 className="text-4xl font-outfit font-bold mb-8 text-pearl-white">Naia Privacy Policy</h1>
        
        <div className="mb-6 text-slate-grey">
          <p><strong>Effective Date:</strong> 2025-06-03</p>
          <p><strong>Last Updated:</strong> 2025-06-03</p>
        </div>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">1. Introduction</h2>
          <p className="mb-4">
            Welcome to Naia, a streaming platform dedicated to vocal synthesis music. This Privacy Policy explains how we collect, use, process, and protect your personal information when you use our platform at streamnaia.com and our mobile applications (collectively, the "Service").
          </p>
          <p>
            Naia is operated by Naia, a company registered in Sweden. We are committed to protecting your privacy and being transparent about how we handle your data.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">2. Information We Collect</h2>
          
          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">2.1 Information You Provide</h3>
          <p className="mb-4">When you create an account or use our Service, we collect:</p>
          <ul className="mb-6 pl-6 space-y-1">
            <li><strong>Email address</strong> (required for account creation and communication)</li>
            <li><strong>Username</strong> (your chosen display name on the platform)</li>
            <li><strong>Name</strong> (for account identification and personalization)</li>
          </ul>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">2.2 Information We Plan to Collect (Future Implementation)</h3>
          <p className="mb-4">
            We are transparent that during our current beta period, we do not collect the following data. However, in future versions of our platform, we plan to collect:
          </p>
          <ul className="mb-4 pl-6 space-y-1">
            <li><strong>Listening history</strong> (songs played, duration, frequency)</li>
            <li><strong>Device information</strong> (device type, operating system, browser information)</li>
          </ul>
          <p className="mb-6">
            This future data collection will be implemented to enable proper artist compensation and improve your experience. We will notify all users before implementing these changes and update this Privacy Policy accordingly.
          </p>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">2.3 Automatically Collected Information</h3>
          <ul className="mb-6 pl-6 space-y-1">
            <li><strong>Authentication cookies</strong> (essential for keeping you logged in)</li>
            <li><strong>Basic usage data</strong> (necessary for platform functionality)</li>
          </ul>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">2.4 Information We Do NOT Collect</h3>
          <ul className="mb-6 pl-6 space-y-1">
            <li>Location data</li>
            <li>Contact lists</li>
            <li>Social media information</li>
            <li>Browsing history outside our platform</li>
            <li>Marketing cookies or tracking pixels</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">3. How We Use Your Information</h2>
          <p className="mb-4">We use your information for the following purposes:</p>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">3.1 Current Uses</h3>
          <ul className="mb-6 pl-6 space-y-1">
            <li><strong>Account management:</strong> Creating and maintaining your account</li>
            <li><strong>Service provision:</strong> Enabling you to stream music and use platform features</li>
            <li><strong>Communication:</strong> Sending important updates about your account or our service</li>
            <li><strong>Customer support:</strong> Responding to your inquiries and providing assistance</li>
            <li><strong>Platform security:</strong> Preventing fraud and protecting against abuse</li>
          </ul>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">3.2 Future Uses (When Implemented)</h3>
          <ul className="mb-6 pl-6 space-y-1">
            <li><strong>Artist compensation:</strong> Calculating royalty payments based on listening data</li>
            <li><strong>Service improvement:</strong> Analyzing usage patterns to enhance our platform</li>
            <li><strong>Personalization:</strong> Providing music recommendations and customized content</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">4. Legal Basis for Processing (GDPR)</h2>
          <p className="mb-4">Under the General Data Protection Regulation (GDPR), our legal bases for processing your personal data are:</p>
          <ul className="mb-6 pl-6 space-y-1">
            <li><strong>Contract performance:</strong> Processing necessary to provide our service</li>
            <li><strong>Legitimate interests:</strong> Improving our platform and preventing fraud</li>
            <li><strong>Consent:</strong> For any optional features or communications (where applicable)</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">5. Data Sharing and Disclosure</h2>
          
          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">5.1 We Do NOT Share Your Data</h3>
          <p className="mb-4">We do not sell, rent, or share your personal information with third parties for their marketing purposes.</p>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">5.2 Service Providers</h3>
          <p className="mb-4">We work with trusted third-party service providers who help us operate our platform:</p>
          <ul className="mb-4 pl-6 space-y-1">
            <li><strong>Cloudflare Pages:</strong> Web hosting and content delivery</li>
            <li><strong>Cloudflare D1:</strong> Secure database storage for user and content information</li>
            <li><strong>Stripe:</strong> Payment processing for subscriptions</li>
            <li><strong>Squarespace:</strong> Domain registration services</li>
          </ul>
          <p className="mb-6">These providers are contractually obligated to protect your data and may only use it to provide services to us.</p>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">5.3 Legal Requirements</h3>
          <p className="mb-6">We may disclose your information if required by law, court order, or government request, or to protect our rights, safety, or the safety of others.</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">6. Data Security</h2>
          <p className="mb-4">We implement appropriate technical and organizational measures to protect your personal information, including:</p>
          <ul className="mb-4 pl-6 space-y-1">
            <li>Encryption of data in transit and at rest</li>
            <li>Secure authentication systems</li>
            <li>Regular security assessments</li>
            <li>Limited access to personal data on a need-to-know basis</li>
          </ul>
          <p className="mb-6">However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">7. Data Retention</h2>
          <p className="mb-4">We retain your personal information:</p>
          <ul className="mb-4 pl-6 space-y-1">
            <li><strong>Indefinitely</strong> while your account is active</li>
            <li><strong>Until you request deletion</strong> of your account and data</li>
            <li><strong>Until we delete it</strong> in cases of account termination for policy violations</li>
          </ul>
          <p className="mb-6">You have the right to request deletion of your account and personal data at any time.</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">8. Your Rights</h2>
          <p className="mb-4">Under applicable privacy laws, including GDPR, you have the following rights:</p>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">8.1 Access and Portability</h3>
          <ul className="mb-4 pl-6 space-y-1">
            <li>Request access to your personal data</li>
            <li>Receive a copy of your data in a portable format</li>
          </ul>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">8.2 Correction and Deletion</h3>
          <ul className="mb-4 pl-6 space-y-1">
            <li>Correct inaccurate personal information</li>
            <li>Request deletion of your personal data ("right to be forgotten")</li>
          </ul>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">8.3 Processing Limitations</h3>
          <ul className="mb-4 pl-6 space-y-1">
            <li>Object to certain types of processing</li>
            <li>Restrict processing in specific circumstances</li>
            <li>Withdraw consent (where processing is based on consent)</li>
          </ul>

          <h3 className="text-xl font-outfit font-medium mb-3 text-pearl-white">8.4 Exercising Your Rights</h3>
          <p className="mb-6">To exercise any of these rights, contact us at legal@streamnaia.com. We will respond within 30 days of receiving your request.</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">9. International Data Transfers</h2>
          <p className="mb-6">As a Sweden-based company serving international users, your data may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers in accordance with applicable privacy laws.</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">10. Children's Privacy</h2>
          <p className="mb-6">Our Service is intended for users aged 13 and older. We do not knowingly collect personal information from children under 13. If we become aware that we have collected such information, we will take steps to delete it promptly.</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">11. Advertising</h2>
          <p className="mb-6">Our free tier includes advertisements. However, we do not use tracking technologies for advertising purposes or share your personal data with advertising networks.</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">12. Changes to This Privacy Policy</h2>
          <p className="mb-4">We may update this Privacy Policy from time to time. When we make material changes, we will:</p>
          <ul className="mb-4 pl-6 space-y-1">
            <li>Post the updated policy on our platform</li>
            <li>Notify you via email or platform notification</li>
            <li>Update the "Last Updated" date</li>
          </ul>
          <p className="mb-6">Your continued use of our Service after changes become effective constitutes acceptance of the revised policy.</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">13. Contact Information</h2>
          <p className="mb-4">If you have questions about this Privacy Policy or our privacy practices, please contact us:</p>
          <p className="mb-2"><strong>Email:</strong> legal@streamnaia.com</p>
          <p className="mb-6"><strong>Subject Line:</strong> Privacy Policy Inquiry</p>
          <p className="mb-6">For GDPR-related requests or concerns, you may also contact the Swedish Data Protection Authority (Datainspektionen).</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-outfit font-semibold mb-4 text-pearl-white">14. Governing Law</h2>
          <p className="mb-6">This Privacy Policy is governed by Swedish law and applicable EU regulations, including the GDPR.</p>
        </section>

        <div className="border-t border-white/20 pt-6 mt-12">
          <p className="text-slate-grey italic text-center">
            This Privacy Policy is part of our Terms of Use and should be read in conjunction with those terms.
          </p>
        </div>
      </div>
    </LegalLayout>
  );
} 